import React, { useState, useEffect } from 'react'
import { Form, Dropdown, Message, Icon, Button, Popup } from 'semantic-ui-react'
import { SketchPicker as ColorPicker, ColorResult } from 'react-color'
import WizardComponent from '../../../../components/Modal/WizardComponent'
import ValidationInput from '../../../../components/ValidationInput/ValidationInput'
import { _MIXEDCOLORS } from '../../../../constants/colors'
import { TransferCandidate } from '../../../budget/project-import/types'
import { IWizardProject } from '../../types/wizardData'
import { findBudgetById, mapBudgetToWizardData, formatBudgetOption } from '../utils/budgetDataMapper'
import { validateBudget } from '../utils/budgetValidations'
import { BudgetDetailsPanel } from './BudgetDetailsPanel'
import { getTransferCandidates } from '../../../budget/project-import/api'

const maxLength20ValidationRule = new RegExp("^.{0,20}$")
const invalidMaxLength20ValidationRuleMsg = 'Max number of characters exceeded. There are only 20 chars allowed.'
const maxLength21 = 21

interface Props {
  open: boolean
  data: IWizardProject
  wizardFinishedSteps: number[]
  setWizardFinishedSteps: (steps: number[]) => void
  setWizardStep: (step: string) => void
  setProjectWizardData: (data: IWizardProject) => void
  onClose: () => void
}

/**
 * Budget Selection Step Component
 *
 * First step in budget import wizard that allows user to:
 * - Select budget from dropdown of transfer candidates
 * - View budget details
 * - Proceed to team selection
 *
 * @example
 * <BudgetSelectionStep {...wizardProps} />
 */
export const BudgetSelectionStep: React.FC<Props> = (props) => {
  const { data, setProjectWizardData, setWizardStep, setWizardFinishedSteps, wizardFinishedSteps, open, onClose } = props

  const [candidates, setCandidates] = useState<TransferCandidate[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(data.budgetImportSource?.budgetId || null)
  const [loading, setLoading] = useState(false)
  const [projectTitle, setProjectTitle] = useState(data.name)
  const [projectColor, setProjectColor] = useState(data.color)

  useEffect(() => {
    loadCandidates()
  }, [])

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * _MIXEDCOLORS.length)
    setProjectColor(data.color || _MIXEDCOLORS[randomIndex])
  }, [data.color])

  /**
   * Loads transfer candidates from API
   */
  const loadCandidates = async (): Promise<void> => {
    setLoading(true)
    const data = await getTransferCandidates()
    setCandidates(data)
    setLoading(false)
  }

  /**
   * Handles budget selection
   * @param budgetId - Selected budget ID
   */
  const handleSelect = (budgetId: number): void => {
    setSelectedId(budgetId)
    const budget = findBudgetById(budgetId, candidates)
    validateBudget(budget)
    const wizardData = mapBudgetToWizardData(budget)
    setProjectWizardData({ ...data, ...wizardData, name: projectTitle, color: projectColor })
  }

  /**
   * Changes project color
   * @param color - Selected color hex value
   */
  const changeColor = (color: ColorResult['hex']): void => {
    setProjectColor(color)
  }

  /**
   * Renders color picker
   */
  const renderColorPicker = (): JSX.Element => {
    return (
      <ColorPicker disableAlpha presetColors={_MIXEDCOLORS} color={projectColor} onChange={color => changeColor(color.hex)} />
    )
  }

  /**
   * Renders color picker popup
   */
  const renderPopup = (): JSX.Element => {
    return (
      <Popup
        id='projectColor'
        basic
        style={{ marginLeft: '-1.5rem' }}
        trigger={
          <Icon
            name='square'
            size='large'
            style={{ zIndex: 1, color: projectColor, position: 'relative', marginTop: '2.9rem', marginLeft: '-3rem' }}
          />
        }
        content={renderColorPicker()}
        on='click'
        position='bottom left'
        hideOnScroll
      />
    )
  }

  /**
   * Handles next step navigation
   */
  const goToNextStep = (): void => {
    const updatedData = {
      ...data,
      name: projectTitle,
      color: projectColor
    }
    if (!wizardFinishedSteps.includes(1)) {
      setWizardFinishedSteps([1])
    }
    setProjectWizardData(updatedData)
    setWizardStep('budget-import-select-team')
  }

  /**
   * Handles back navigation
   */
  const goToPrevStep = (): void => {
    setProjectWizardData({ ...data, type: '' })
    setWizardStep('step-one')
  }

  /**
   * Renders wizard content
   */
  const renderContent = (): JSX.Element => {
    return (
      <>
        <Form>
          <Form.Group>
            <ValidationInput
              fluid
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
              placeholder="Enter project title"
              validatePattern={maxLength20ValidationRule}
              invalidMessage={invalidMaxLength20ValidationRuleMsg}
              maxLength={maxLength21}
              marginBottom='10px'
              label={
                <label style={{ display: 'flex', marginBottom: '.5rem', alignItems: 'center' }}>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>Project title</p>
                </label>
              }
            />
            {renderPopup()}
          </Form.Group>

          <Form.Field required>
            <label>Import from Study Budgeting</label>
            <Dropdown
              selection
              fluid
              placeholder="Select a budget..."
              loading={loading}
              value={selectedId || ''}
              options={candidates.map(b => ({
                key: b.id,
                text: formatBudgetOption(b),
                value: b.id
              }))}
              onChange={(e, { value }) => handleSelect(value as number)}
            />
          </Form.Field>

          {selectedId && (
            <BudgetDetailsPanel budget={findBudgetById(selectedId, candidates)} />
          )}

          <Message info>
            <Icon name="info circle" />
            Client and dates will be automatically populated from the selected budget.
          </Message>
        </Form>
      </>
    )
  }

  /**
   * Renders wizard footer
   */
  const renderFooter = (): JSX.Element => {
    return (
      <div>
        <Button
          inverted
          color='grey'
          size='small'
          onClick={goToPrevStep}
          content='Back'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0, color: '#747678', borderColor: '#BEC8C3', marginRight: '1rem' }}
        />
        <Button
          primary={true}
          disabled={!selectedId || !projectTitle || projectTitle.length >= maxLength21}
          size='small'
          onClick={goToNextStep}
          content='Next'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0 }}
        />
      </div>
    )
  }

  return (
    <WizardComponent
      headerContent="Enter Base Data"
      removeSteps={true}
      finishedSteps={wizardFinishedSteps}
      open={open}
      close={onClose}
      content={renderContent()}
      footer={renderFooter()}
      contentPadding='0 48px 2rem'
      actionPadding='20px 48px'
    />
  )
}

export default BudgetSelectionStep
